import { type FormInstance } from 'antd';
import type { StoreApi } from 'zustand';

import { changePassword } from '~/services/auth/AuthService';
import type { IChangePasswordParams } from '~/types/api/Auth';
import type { IAuthStore } from './useAuthStore';

export type IChangePasswordFormFilter = {
  password: string;
  confirmPassword: string;
};

export type IChangePasswordActions = {
  resetStates: () => void;
  setFormFilterInstance: (form: Partial<FormInstance<IChangePasswordFormFilter>>) => void;
  getParams: (temporaryToken: string) => IChangePasswordParams;
  handleSubmit: (temporaryToken: string) => Promise<void>;
};

export type IChangePasswordState = {
  isLoading: boolean;
  formFilterInstance: Partial<FormInstance<IChangePasswordFormFilter>> | null;
};

export type IChangePasswordSlice = IChangePasswordState & IChangePasswordActions;

const useChangePasswordSlice = (
  set: StoreApi<IAuthStore>['setState'],
  get: StoreApi<IAuthStore>['getState'],
): IChangePasswordSlice => ({
  // States
  isLoading: false,
  formFilterInstance: null,

  // Setters
  setFormFilterInstance: form => {
    get().setData(state => {
      state.changePasswordSlice.formFilterInstance = form;
    });
  },

  // Actions
  resetStates: () => {
    get().setData(state => {
      state.changePasswordSlice.isLoading = false;
      state.changePasswordSlice.formFilterInstance = null;
    });
  },
  getParams: (temporaryToken: string) => {
    const { formFilterInstance } = get().changePasswordSlice;
    if (!formFilterInstance?.getFieldsValue) {
      throw new Error('Instância do formulário não está definida.');
    }

    const formValues = formFilterInstance.getFieldsValue();

    if (formValues.password !== formValues.confirmPassword) {
      throw new Error('As senhas devem ser iguais!');
    }

    return {
      password: formValues.password,
      confirmPassword: formValues.confirmPassword,
      temporaryToken,
    } as IChangePasswordParams;
  },
  handleSubmit: async (temporaryToken: string) => {
    const { formFilterInstance } = get().changePasswordSlice;
    if (!formFilterInstance) return;

    get().setData(state => {
      state.changePasswordSlice.isLoading = true;
    });

    try {
      const params = get().changePasswordSlice.getParams(temporaryToken);
      await changePassword(params);
      return Promise.resolve();
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    } finally {
      get().setData(state => {
        state.changePasswordSlice.isLoading = false;
      });
    }
  },
});

export default useChangePasswordSlice;
